import React, { forwardRef, useEffect, useRef, useState } from 'react';
import './StyledInput.css';
import { formatNumber, toInputText, filterNumberText, parseNumberText, numberSeparators } from './numberFormat';

// The one number field for the whole app (ui-architecture.md "Numbers and
// Currency"). Shows and takes the company Number Format (Company Settings >
// Localisation), so a company on 1.234,56 types a price as 1.234,56. A
// native <input type="number"> can't: it follows the viewer's browser
// language and can't show a thousands separator at all.
//
// Same props as <input type="number">: value/defaultValue and onChange stay
// the plain '1234.56' form ('' for blank), and onChange/onBlur get an event
// whose target.value is that plain form. While focused the field shows the
// text as typed; once it loses focus it shows the value with thousands
// separators. onChange fires on every keystroke that leaves a whole number
// (never on a half-typed '1.2.'); on blur, anything that still isn't a
// number goes back to the last good value.
//
// `decimals` fixes the places shown when not focused (2 for money);
// `minDecimals`/`maxDecimals` bound them instead, showing only what the
// value has (a cost of 12.3456 with minDecimals 2 and maxDecimals 4, a
// quantity or weight). `decimals={0}` makes an integer-only field. A
// `min` of 0 or more blocks a minus sign. `step` is accepted and ignored.
// `stepper` adds small up/down arrows inside the right edge (and makes the
// Up/Down keys step) by 1, kept within `min`/`max`. Clicking an arrow
// focuses the field, so onBlur still fires once when the user moves on.
function numberEvent(value, name, id) {
  const target = { value, name, id };
  return { target, currentTarget: target, preventDefault() {}, stopPropagation() {} };
}

const NumberInput = forwardRef(function NumberInput({
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  decimals,
  minDecimals,
  maxDecimals,
  min,
  max,
  step, // eslint-disable-line no-unused-vars
  stepper,
  className = 'styled-input',
  style,
  name,
  id,
  ...rest
}, ref) {
  const isControlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(defaultValue ?? '');
  const current = isControlled ? (value ?? '') : innerValue;
  const allowNegative = min === undefined || min === null || min === '' || Number(min) < 0;

  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => toInputText(current));

  // Show the stored value whenever nobody is typing — a parent resetting
  // the field, or a blur reverting a half-typed number.
  useEffect(() => {
    if (!focused) setText(toInputText(current));
  }, [current, focused]);

  const inputRef = useRef(null);
  const setRefs = (el) => {
    inputRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  const emit = (plain) => {
    if (!isControlled) setInnerValue(plain);
    if (onChange) onChange(numberEvent(plain, name, id));
  };

  const handleChange = (e) => {
    const next = filterNumberText(e.target.value, { allowNegative, decimals });
    setText(next);
    const plain = parseNumberText(next);
    if (plain !== null && plain !== String(current)) emit(plain);
  };

  const handleBlur = () => {
    setFocused(false);
    const plain = parseNumberText(text);
    const good = plain === null ? String(current) : plain;
    if (plain === null) setText(toInputText(current));
    if (onBlur) onBlur(numberEvent(good, name, id));
  };

  const stepBy = (dir) => {
    const typed = parseNumberText(focused ? text : toInputText(current));
    let next = (Number(typed ?? current) || 0) + dir;
    if (min !== undefined && min !== null && min !== '') next = Math.max(Number(min), next);
    if (max !== undefined && max !== null && max !== '') next = Math.min(Number(max), next);
    const plain = String(Number(next.toFixed(decimals ?? maxDecimals ?? 10)));
    setText(toInputText(plain));
    if (plain !== String(current)) emit(plain);
  };

  const handleKeyDown = (e) => {
    if (stepper && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      stepBy(e.key === 'ArrowUp' ? 1 : -1);
    }
    if (rest.onKeyDown) rest.onKeyDown(e);
  };

  const shown = focused
    ? text
    : formatNumber(current, decimals !== undefined ? { decimals } : { minDecimals, maxDecimals: maxDecimals ?? 10 });
  const invalid = focused && text !== '' && parseNumberText(text) === null;
  const example = formatNumber(1234.5, { decimals: decimals ?? 2 });

  const showStepper = stepper && !rest.disabled && !rest.readOnly;
  const inputStyle = invalid ? { ...style, borderColor: '#b91c1c' } : style;

  const input = (
    <input
      {...rest}
      ref={setRefs}
      type="text"
      inputMode={decimals === 0 ? 'numeric' : 'decimal'}
      autoComplete="off"
      id={id}
      name={name}
      className={className}
      style={showStepper ? { ...inputStyle, paddingRight: 18 } : inputStyle}
      value={shown}
      onFocus={(e) => { setFocused(true); setText(toInputText(current)); if (onFocus) onFocus(e); }}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      aria-invalid={invalid || undefined}
      title={invalid ? `Enter a number like ${example} (decimal ${numberSeparators().decimal === ',' ? 'comma' : 'point'})` : rest.title}
    />
  );
  if (!showStepper) return input;

  // preventDefault on mousedown keeps (or puts) focus in the field, so a run
  // of clicks is one edit and onBlur fires once afterwards.
  const arrow = (dir, glyph, label) => (
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      title={label}
      onMouseDown={(e) => { e.preventDefault(); if (inputRef.current) inputRef.current.focus(); stepBy(dir); }}
      style={{ flex: 1, padding: 0, border: 'none', background: 'transparent', color: '#666', fontSize: 7, lineHeight: 1, cursor: 'pointer' }}
    >
      {glyph}
    </button>
  );
  return (
    <span style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle', width: style?.width }}>
      {input}
      <span style={{ position: 'absolute', top: 2, bottom: 2, right: 2, width: 14, display: 'flex', flexDirection: 'column' }}>
        {arrow(1, '▲', 'Increase')}
        {arrow(-1, '▼', 'Decrease')}
      </span>
    </span>
  );
});

export default NumberInput;
